import {
  expandAllFeature,
  hotkeysCoreFeature,
  syncDataLoaderFeature,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";
import { InputAdornment, TextField } from "@mui/material";
import classNames from "classnames";
import { ChevronRight, Search } from "lucide-react";
import * as React from "react";

import {
  buildMetricTree,
  type MetricTreeNode,
} from "@/shared/domain/metrics/metricTree";
import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";
import { fuzzyFilter } from "@/shared/text/fuzzyFilter";
import { FuzzyHighlight } from "@/shared/text/FuzzyHighlight";

import styles from "./MetricTree.module.css";

const SEARCH_DEBOUNCE_MS = 150;

/**
 * headless-tree's sync data loader must return an item for *every* id it tracks.
 * The domain index returns `undefined` for ids it doesn't know — which happens
 * transiently when retained tree state (focus/expansion) references a node a
 * rebuild has just pruned. This placeholder keeps the loader total so the tree
 * never reads a property off `undefined`. It isn't rendered (the stale row is
 * reconciled away on the synchronous rebuild), so the blank label is fine.
 */
function placeholderMetricNode(id: string): MetricTreeNode {
  return {
    id,
    label: "",
    depth: 0,
    isLeaf: false,
    hasChildren: false,
    leafCount: 0,
    data: undefined,
  };
}

export interface MetricTreeProps {
  /** Full list of metric names (dot-delimited). The tree structure is derived
   *  client-side from these. */
  names: string[];
  /** The currently-selected metric path, or null. Highlights the matching leaf. */
  selectedMetric: string | null;
  /** Called when the user picks a leaf (a full metric path). */
  onSelect: (name: string) => void;
}

/**
 * Left pane of the metrics filter: a debounced fuzzy search over a collapsible
 * tree of dot-delimited metric names. Tree state, keyboard navigation and ARIA
 * come from `headless-tree`; we render our own rows.
 *
 * Search prunes the data to matching leaves + ancestors and expands the whole
 * pruned tree (`expandAll`); clearing the query collapses back to top level.
 */
export function MetricTree({
  names,
  selectedMetric,
  onSelect,
}: MetricTreeProps) {
  const [query, setQuery] = React.useState("");
  const debouncedSetQuery = useDebouncedCallback(setQuery, SEARCH_DEBOUNCE_MS);

  // MUI Popover's Grow transition clobbers `autoFocus` on child inputs; claim
  // focus one frame after mount instead.
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(function focusSearchInput() {
    const handle = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(handle);
  }, []);

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") {
      return;
    }
    const value = event.currentTarget.value;
    const matches = fuzzyFilter(value, names);
    if (matches.length === 0) {
      return;
    }
    event.preventDefault();
    // Flush the debounce so the tree reflects the same text the match was
    // computed from; pressing Enter before the 150ms debounce settles would
    // otherwise select a metric not yet visible in the (stale) tree. `matches`
    // is ranked best-first, so `[0]` is the strongest fuzzy match.
    debouncedSetQuery.cancel();
    setQuery(value);
    onSelect(matches[0]);
  };

  const isSearching = query.trim().length > 0;
  // `fuzzyFilter` returns all names unchanged for an empty query, so it covers
  // both the searching and idle cases.
  const filtered = React.useMemo(
    () => fuzzyFilter(query, names),
    [names, query],
  );
  const index = React.useMemo(() => buildMetricTree(filtered), [filtered]);

  const tree = useTree<MetricTreeNode>({
    rootItemId: index.rootItemId,
    getItemName: (item) => item.getItemData().label,
    isItemFolder: (item) => item.getItemData().hasChildren,
    dataLoader: {
      getItem: (id) => index.getItem(id) ?? placeholderMetricNode(id),
      getChildren: index.getChildren,
    },
    onPrimaryAction: (item) => {
      if (item.getItemData().isLeaf) {
        onSelect(item.getItemData().id);
      }
    },
    features: [syncDataLoaderFeature, hotkeysCoreFeature, expandAllFeature],
  });

  // `useTree` keeps a stable instance and re-derives structure only on
  // `rebuildTree()`. When the (filtered) data changes, rebuild and set
  // expansion: expand the whole pruned tree while searching, collapse on clear.
  // `expandAll()`/`collapseAll()` resolve synchronously under the sync data
  // loader, so the returned promise is intentionally discarded (no cancel token
  // needed). `isSearching` is in the deps only for exhaustive-deps; it's derived
  // from `query` (which already feeds `index`), so it never forces an extra run.
  React.useEffect(() => {
    tree.rebuildTree();
    if (isSearching) {
      void tree.expandAll();
    } else {
      void tree.collapseAll();
    }
  }, [index, isSearching, tree]);

  return (
    <div className={styles.picker}>
      <div className={styles.search}>
        <TextField
          inputRef={searchInputRef}
          autoComplete="off"
          size="small"
          type="search"
          className={styles.searchInput}
          placeholder="Search metrics…"
          onChange={(event) => debouncedSetQuery(event.target.value)}
          inputProps={{
            "aria-label": "Search metrics",
            onKeyDown: handleSearchKeyDown,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={14} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {/* Count line only when there are matches; the empty state below owns the
          zero case (so we never show two "no metrics" messages at once). */}
      {isSearching && filtered.length > 0 && (
        <div className={styles.resultCount} role="status">
          {`${filtered.length} of ${names.length} metrics`}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className={styles.empty}>
          {isSearching ? "No matching metrics" : "No metrics defined"}
        </div>
      ) : (
        <div {...tree.getContainerProps("Metrics")} className={styles.tree}>
          {tree.getItems().map((item) => {
            const node = item.getItemData();
            const isFolder = item.isFolder();
            return (
              <button
                key={item.getId()}
                {...item.getProps()}
                className={classNames(styles.row, {
                  [styles.rowSelected]:
                    node.isLeaf && node.id === selectedMetric,
                })}
                style={
                  {
                    "--indent-depth": item.getItemMeta().level,
                  } as React.CSSProperties
                }
              >
                {isFolder ? (
                  <ChevronRight
                    className={classNames(styles.chevron, {
                      [styles.chevronOpen]: item.isExpanded(),
                    })}
                    aria-hidden="true"
                  />
                ) : (
                  <span className={styles.iconSpacer} aria-hidden="true" />
                )}
                {/* Native title shows the full dotted path on hover so a
                    truncated label is still readable — no tooltip component. */}
                <span
                  className={classNames(styles.label, "truncate")}
                  title={node.id}
                >
                  {/* Fuzzy-match against the full path (`node.id`) but render
                      only the label slice; `partial` highlights the prefix on
                      folder rows whose path doesn't cover the whole query. */}
                  <FuzzyHighlight
                    text={node.id}
                    query={query}
                    partial
                    slice={[node.id.length - node.label.length, node.id.length]}
                    markClassName={styles.mark}
                  />
                </span>
                {isFolder && (
                  <span className={styles.count}>{node.leafCount}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
